import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Sparkles, AlertTriangle, TrendingUp, Lightbulb } from 'lucide-react';
import { cn } from '../../lib/utils';

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
  'X-Company-ID': localStorage.getItem('company_id') || '',
});

const PRIORITY_STYLES = {
  critical: { ring: 'ring-red-200 dark:ring-red-900', dot: 'bg-red-500', icon: AlertTriangle, text: 'text-red-700 dark:text-red-300' },
  high:     { ring: 'ring-amber-200 dark:ring-amber-900', dot: 'bg-amber-500', icon: AlertTriangle, text: 'text-amber-700 dark:text-amber-300' },
  medium:   { ring: 'ring-blue-200 dark:ring-blue-900', dot: 'bg-blue-500', icon: TrendingUp, text: 'text-blue-700 dark:text-blue-300' },
  low:      { ring: 'ring-neutral-200 dark:ring-neutral-800', dot: 'bg-neutral-400', icon: Lightbulb, text: 'text-neutral-700 dark:text-neutral-200' },
};

/**
 * AIInsightsStrip — pulls `/api/ai/insights` + `/api/ai/anomalies` and shows
 * up to `max` cards. Each card deep-links to the relevant page.
 */
export default function AIInsightsStrip({ max = 3, className = '' }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetch = useCallback(async () => {
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const [i, a] = await Promise.allSettled([
        axios.get(`${base}/api/ai/insights`, { headers: getAuthHeaders() }),
        axios.get(`${base}/api/ai/anomalies`, { headers: getAuthHeaders() }),
      ]);

      const fallbackLinkFor = (category) => {
        switch (category) {
          case 'inventory': return '/products';
          case 'cashflow':  return '/invoices';
          case 'sales':     return '/invoices';
          case 'customers': return '/customers';
          case 'expenses':  return '/expenses';
          default:          return '/dashboard';
        }
      };

      const merged = [];
      if (i.status === 'fulfilled' && Array.isArray(i.value.data)) {
        i.value.data.forEach((it) => merged.push({
          id: it.id,
          title: it.title,
          description: it.description || it.action || '',
          priority: it.priority || 'medium',
          // UX.1 — prefer backend-supplied deep link when present
          link: it.link || fallbackLinkFor(it.category),
        }));
      }
      if (a.status === 'fulfilled' && Array.isArray(a.value.data?.anomalies)) {
        a.value.data.anomalies.forEach((an) => merged.push({
          id: an.id,
          title: an.title,
          description: an.message || '',
          priority: an.severity === 'warning' ? 'high' : 'medium',
          link: an.link || '/dashboard',
        }));
      }
      setItems(merged.slice(0, max));
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'تعذر تحميل التوصيات');
    } finally {
      setLoading(false);
    }
  }, [max]);

  useEffect(() => { fetch(); }, [fetch]);

  return (
    <div
      className={cn(
        'rounded-lg bg-gradient-to-l from-violet-50 via-white to-primary-50 dark:from-violet-950/40 dark:via-neutral-900 dark:to-primary-950/40',
        'border border-violet-100 dark:border-violet-900/40 shadow-sm p-4',
        className,
      )}
      dir="rtl"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-bold text-neutral-900 dark:text-neutral-50 flex items-center gap-2">
          <Sparkles size={16} className="text-violet-600 dark:text-violet-400" aria-hidden="true" />
          توصيات ذكية
        </h3>
        {!loading && !error && items.length > 0 && (
          <span className="text-[11px] text-violet-700 dark:text-violet-300 bg-violet-100 dark:bg-violet-950/60 px-2 py-0.5 rounded-full">
            {items.length}
          </span>
        )}
      </div>

      {loading && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="animate-pulse rounded-md bg-white/70 dark:bg-neutral-900/70 h-20" />
          ))}
        </div>
      )}

      {!loading && error && (
        <p className="text-sm text-danger-600 dark:text-danger-400">{error}</p>
      )}

      {!loading && !error && items.length === 0 && (
        <p className="text-sm text-neutral-500 dark:text-neutral-400 py-2">
          لا توجد توصيات حالياً — البيانات قيد التجميع.
        </p>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {items.map((it, idx) => {
            const p = PRIORITY_STYLES[it.priority] || PRIORITY_STYLES.medium;
            const Icon = p.icon;
            return (
              <Link
                key={it.id || idx}
                to={it.link}
                className={cn(
                  'group bg-white dark:bg-neutral-900 rounded-md p-3 ring-1 transition-all hover:shadow-md hover:-translate-y-0.5',
                  p.ring,
                )}
              >
                <div className="flex items-start gap-2">
                  <span className={cn('flex h-7 w-7 items-center justify-center rounded-lg flex-shrink-0 bg-white dark:bg-neutral-800')}>
                    <Icon size={14} className={p.text} aria-hidden="true" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className={cn('text-sm font-semibold leading-snug line-clamp-2', p.text)}>
                      {it.title}
                    </p>
                    {it.description && (
                      <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-1 line-clamp-2">
                        {it.description}
                      </p>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
